import { Injectable } from '@angular/core';
import { OperationRegisterService } from '@core/services/core/operation-register.service';
import { AddressHelperService } from '@core/services/helpers/address-helper.service';
import { CollectiveLifePoliciesService } from '@core/services/http-services/collective-life-policies.service';
import { DocumentTypes } from '@models/document';
import { AllFeatureFlags } from '@models/mmobile';
import { PolicyCollectiveLife } from '@models/policy-collective-life';
import { PolicyOperativeDocumentsConfig } from '@models/policy-config';
import { PolicyInvestmentNova } from '@models/policy-investment-nova';
import * as moment from 'moment';
import { BehaviorSubject, forkJoin, from, Observable, of, throwError } from 'rxjs';
import { catchError, first, map, switchMap, tap } from 'rxjs/operators';
import { Address } from '../../models/address';
import {
  Document,
  Policy,
  PolicyBase,
  PolicyBaseType,
  PolicyExpedient,
  PolicyReceipt,
  PolicyReportDocumentType,
  PolicySinister,
  POLICY_BASE_TYPES
} from '../../models/policy';
import { PolicyAccident } from '../../models/policy-accident';
import { PolicyCar, PolicyCarType, SuperbonusOfferType, SUPERBONUS_LQ_STATUS_AVAILABILITY_TIME_IN_MONTHS } from '../../models/policy-car';
import { PolicyDeath } from '../../models/policy-death';
import { PolicyHealth, PolicyHealthInsured } from '../../models/policy-health';
import { PolicyHome } from '../../models/policy-home';
import { PolicyLife } from '../../models/policy-life';
import { PolicyOther } from '../../models/policy-other';
import { PolicyPension } from '../../models/policy-pension';
import { PolicySaving } from '../../models/policy-saving';
import { LifeCyclePolicyType, PolicyBaseEntry } from '../../models/products';
import { addIsLifetimePolicy, createObjectOfPoliciesById, getAge, padZeros } from '../../utils/utils';
import { PoliciesContextService } from '../context/policies-context.service';
import { AccidentPoliciesService } from '../http-services/accident-policies.service';
import { CarPoliciesService } from '../http-services/car-policies.service';
import { DeathPoliciesService } from '../http-services/death-policies.service';
import { HealthPoliciesService } from '../http-services/health-policies.service';
import { HomePoliciesService } from '../http-services/home-policies.service';
import { InvestmentNovaPoliciesService } from '../http-services/investment-nova-policies.service';
import { LifePoliciesService } from '../http-services/life-policies.service';
import { OtherPoliciesService } from '../http-services/other-policies.service';
import { PensionPoliciesService } from '../http-services/pension-policies.service';
import { SavingPoliciesService } from '../http-services/saving-policies.service';
import { AuthMirrorService } from './auth/auth-mirror.service';
import { MMobileHelperService } from './mmobile-helper.service';
import { PoliciesBaseService } from './policies-base.service';



@Injectable({
  providedIn: 'root'
})
export class PoliciesHelperService {

  private userHasCarPolicies = new BehaviorSubject<boolean>(false);
  private userHasHealthPolicies = new BehaviorSubject<boolean>(false);
  private userHasMugejuProduct = new BehaviorSubject<boolean>(false);
  public maxPrefetchPolicies = new BehaviorSubject<number>(0);

  constructor(
    private policiesBaseService: PoliciesBaseService,
    private carPoliciesService: CarPoliciesService,
    private homePoliciesService: HomePoliciesService,
    private otherPoliciesService: OtherPoliciesService,
    private pensionPoliciesService: PensionPoliciesService,
    private investmentNovaPoliciesService: InvestmentNovaPoliciesService,
    private lifePoliciesService: LifePoliciesService,
    private collectiveLifePoliciesService: CollectiveLifePoliciesService,
    private savingPoliciesService: SavingPoliciesService,
    private healthPoliciesService: HealthPoliciesService,
    private accidentPoliciesService: AccidentPoliciesService,
    private deathPoliciesService: DeathPoliciesService,
    private mmobileHelper: MMobileHelperService,
    private policiesContext: PoliciesContextService,
    private authMirror: AuthMirrorService,
    private addressHelper: AddressHelperService,
    private or: OperationRegisterService
  ) {}

  checkUserHasHealthPolicies$(){
    return this.userHasHealthPolicies.asObservable();
  }

  checkUserHasCarPolicies$(){
    return this.userHasCarPolicies.asObservable();
  }

  checkUserHasMugejuProduct$(){
    return this.userHasMugejuProduct.asObservable();
  }

  getAllPoliciesById() {
    const policies$ = POLICY_BASE_TYPES.map(
      type => this.getPolicies(type) as Observable<PolicyBase[]>
    );
    return forkJoin(policies$)
      .toPromise()
      .then(obsArr => [].concat(obsArr) as PolicyBase[])
      .then(createObjectOfPoliciesById);
  }

  getPolicyBaseEntries(policyBaseTypes: PolicyBaseType[]) {
    return forkJoin(this.getPolicyBaseEntriesObservables(policyBaseTypes));
  }

  private getPolicyBaseEntriesObservables(policyBaseTypes: PolicyBaseType[]) {
    return (policyBaseTypes || []).map(policyBaseType =>
      this.getPolicyBaseEntryObservable(policyBaseType)
    );
  }

  private getPolicyBaseEntryObservable(policyBaseType: PolicyBaseType) {
    return this.getPoliciesByPolicyBaseType(policyBaseType).pipe(
      map(policiesList => {
        return { policyBaseType, policiesList } as PolicyBaseEntry;
      }),
      catchError(err => {
        return of({
          policyBaseType,
          policiesListError: true,
          policiesListErrorDetail: {
            type: policyBaseType,
            errorDetail: err?.error || 'Generic Error'
          }
        } as PolicyBaseEntry);
      })
    );
  }

  getPoliciesByPolicyBaseType(policyBaseType: PolicyBaseType) {
    const policiesFromState =
      this.policiesContext.getPoliciesByBaseType(policyBaseType);
    if (policiesFromState) {
      return of(policiesFromState);
    }

    let policies$: Observable<Policy[]> = of([] as Policy[]);
    switch (policyBaseType) {
      case 'car': {
        policies$ = this.policiesBaseService
          .getCarPolicies()
          .pipe(map(({ carPolicies }) => carPolicies));
        break;
      }
      case 'home': {
        policies$ = this.policiesBaseService
          .getHomeOtherPolicies()
          .pipe(
            tap((res) => {
              if (res.maxOperationalPrefetchPolicies) {
                this.maxPrefetchPolicies.next(res.maxOperationalPrefetchPolicies);
              }
            }),
            map(({ homePolicies }) => homePolicies)
          );
        break;
      }
      case 'other': {
        policies$ = this.policiesBaseService
          .getHomeOtherPolicies()
          .pipe(map(({ otherPolicies }) => otherPolicies));
        break;
      }
      case 'life': {
        policies$ = this.policiesBaseService
          .getLifePolicies()
          .pipe(map(({ lifePolicies }) => lifePolicies));
        break;
      }
      case 'health': {
        policies$ = this.policiesBaseService
          .getHealthPolicies()
          .pipe(map(({ healthPolicies }) => healthPolicies));
        break;
      }
      default:
        break;
    }

    return policies$.pipe(
      map(policies => {
        return policies || [];
      }),
      map(policies => {
        return policies.map(policy => {
          return {
            ...policy,
            policyBaseType
          } as Policy;
        });
      }),
      tap(policies =>
        this.policiesContext.addPolicies(policyBaseType, policies)
      )
    );
  }

  getPolicies(policyBaseType: PolicyBaseType) {
    let isEnabled = this.mmobileHelper.checkIsFeatureEnabled(
      `policiesList__${policyBaseType}`
    );
    if (isEnabled === undefined || isEnabled === null) {
      isEnabled = true;
    }
    if (!isEnabled) {
      return throwError({
        message: `Listado de pólizas de ${policyBaseType} desactivado por Mmobile`
      });
    }

    const policiesFromState =
      this.policiesContext.getPoliciesByBaseType(policyBaseType);
    if (policiesFromState) {
      return of(policiesFromState);
    }

    const aciCompaniesEnabledDepartments =
      this.mmobileHelper.getCustomConfigData('ACICompaniesEnabledDepartments');
    let policies$ = of<Policy[]>([]);
    switch (policyBaseType) {
      case 'car': {
        policies$ = this.policiesBaseService.getCarPolicies().pipe(
          map(({ carPolicies }) => carPolicies),
          map((policies: PolicyCar[]) => {
            if (policies && policies.length) {
              this.userHasCarPolicies.next(true);
              policies.forEach(
                policy =>
                  (policy.hasSuperbonus = this.checkPolicyHasSuperbonus(policy))
              );
            } else {
              this.userHasCarPolicies.next(false);
            }
            return policies;
          })
        );
        break;
      }
      case 'home': {
        policies$ = this.policiesBaseService
          .getHomeOtherPolicies()
          .pipe(map(({ homePolicies }) => homePolicies));
        break;
      }
      case 'other': {
        policies$ = this.policiesBaseService
          .getHomeOtherPolicies()
          .pipe(
            map(({ otherPolicies }) =>
              ((otherPolicies || []) as PolicyOther[])
                .filter(
                  policy =>
                    !this.deathPoliciesService.filterDeathPolicy(policy) &&
                    !this.filterAccidentPolicyByMMobile(policy)
                )
                .filter(
                  (availablePolicy: PolicyOther) =>
                    availablePolicy.freelancer ||
                    !availablePolicy.aciCompany ||
                    this.aciCompaniesEnabledDepartmentsFilter(availablePolicy, aciCompaniesEnabledDepartments)
                )
            )
          );
        break;
      }
      case 'pension': {
        policies$ = this.policiesBaseService
          .getPensionInvestmentPolicies()
          .pipe(map(({ pensionPolicies }) => pensionPolicies), map((pensionPolicies: PolicyPension[]) => {
            if (pensionPolicies.length === 0) { return []; }
            return pensionPolicies.map(policy => {
                if (policy.belongsToLifeCycle) {
                  return { ...policy, lifeCycleCode: policy.lifeCycleCode || LifeCyclePolicyType.FUTURE_PROGRAM };
                }
                return policy;
              });
            })
          );
        break;
      }
      case 'investment': {
        policies$ = this.policiesBaseService
          .getPensionInvestmentPolicies()
          .pipe(map(({ investmentPolicies }) => investmentPolicies));
        break;
      }
      case 'investment-nova': {
        policies$ = this.policiesBaseService
          .getPensionInvestmentPolicies()
          .pipe(map(({ investmentNovaPolicies }) => investmentNovaPolicies));
        break;
      }
      case 'life': {
        policies$ = this.policiesBaseService.getLifePolicies().pipe(
          map(({ lifePolicies }) => lifePolicies),
          addIsLifetimePolicy
        );
        break;
      }
      case 'collective-life': {
        policies$ = this.policiesBaseService.getLifePolicies().pipe(
          map(({ collectivePolicies }) => collectivePolicies),
          addIsLifetimePolicy
        );
        break;
      }
      case 'saving': {
        policies$ = this.policiesBaseService.getLifePolicies().pipe(
          map(({ savingPolicies }) => savingPolicies),
          addIsLifetimePolicy
        );
        break;
      }
      case 'health': {
        policies$ = this.policiesBaseService
          .getHealthPolicies()
          .pipe(
            map(({ healthPolicies }) => {
              if (healthPolicies && healthPolicies.length) {
                this.userHasHealthPolicies.next(true);
                healthPolicies.forEach(
                  policy => {
                    if (policy.productCode === '003'){ this.userHasMugejuProduct.next(true); }
                    else { this.userHasMugejuProduct.next(false); }
                });
              } else {
                this.userHasHealthPolicies.next(false);
                this.userHasMugejuProduct.next(false);
              }
              return healthPolicies;
            })
          );
        break;
      }
      case 'accident': {
        policies$ = this.policiesBaseService
          .getHomeOtherPolicies()
          .pipe(
            map(res =>
              ((res.otherPolicies || []) as PolicyAccident[])
                .filter(policy =>
                  this.filterAccidentPolicyByMMobile(policy)
                )
                .filter(
                  (availablePolicy: PolicyAccident) =>
                    !availablePolicy.aciCompany ||
                    (availablePolicy.aciCompany &&
                      aciCompaniesEnabledDepartments &&
                      aciCompaniesEnabledDepartments.find(
                        (element: string) =>
                          element === availablePolicy.department
                      ))
                )
            )
          );
        break;
      }
      case 'death': {
        policies$ = this.policiesBaseService
          .getHomeOtherPolicies()
          .pipe(
            map(res =>
              ((res.otherPolicies || []) as PolicyDeath[])
                .filter(policy =>
                  this.deathPoliciesService.filterDeathPolicy(policy)
                )
                .filter(
                  (availablePolicy: PolicyDeath) =>
                    !availablePolicy.aciCompany ||
                    (availablePolicy.aciCompany &&
                      aciCompaniesEnabledDepartments &&
                      aciCompaniesEnabledDepartments.find(
                        (element: string) =>
                          element === availablePolicy.department
                      ))
                )
            )
          );
        break;
      }
    }
    return policies$.pipe(
      map(policies => policies || []),
      map(policies => policies.map(policy => ({ ...policy, policyBaseType }))),
      tap(policies =>
        this.policiesContext.addPolicies(policyBaseType, policies)
      )
    );
  }

  getPolicy(policy: Policy): Observable<any> {
    const { policyBaseType } = policy;
    const addBaseType = <T extends object>() =>
      map((p: T) => ({ ...p, policyBaseType }));

    switch (policyBaseType) {
      case 'car': {
        return this.carPoliciesService.getCarPolicy(policy as PolicyCar).pipe(
          map(
            res =>
              res && {
                ...res,
                hasSuperbonus: this.checkPolicyHasSuperbonus(res)
              }
          ),
          addBaseType()
        );
      }
      case 'home': {
        return this.homePoliciesService
          .getHomePolicy(policy as PolicyHome)
          .pipe(addBaseType());
      }
      case 'other': {
        return this.otherPoliciesService
          .getOtherPolicy(policy as PolicyOther)
          .pipe(addBaseType());
      }
      case 'pension': {
        return this.pensionPoliciesService
          .getPensionPolicy(policy as PolicyPension)
          .pipe(addBaseType());
      }
      case 'life': {
        return this.lifePoliciesService
          .getLifePolicy(policy as PolicyLife)
          .pipe(addBaseType(), addIsLifetimePolicy);
      }
      case 'collective-life': {
        return this.collectiveLifePoliciesService
          .getCollectiveLifePolicy(policy as PolicyCollectiveLife)
          .pipe(addBaseType(), addIsLifetimePolicy);
      }
      case 'saving': {
        return this.savingPoliciesService
          .getSavingPolicy(policy as PolicySaving)
          .pipe(addBaseType(), addIsLifetimePolicy);
      }
      case 'health': {
        return this.healthPoliciesService
          .getHealthPolicy(policy as PolicyHealth)
          .pipe(addBaseType());
      }
      case 'accident': {
        return this.accidentPoliciesService
          .getAccidentPolicy(policy as PolicyAccident)
          .pipe(addBaseType());
      }
      case 'death': {
        return this.deathPoliciesService
          .getDeathPolicy(policy as PolicyDeath)
          .pipe(addBaseType());
      }
      default: {
        return of(null);
      }
    }
  }

  getPolicyNovaToken(policy: Policy): Observable<any> {
    if (policy.policyBaseType === 'investment-nova') {
      return this.investmentNovaPoliciesService
        .getInvestmentNovaPolicyToken(policy.policyNumber)
        .pipe(
          map((tokenUrl: string) => {
            return tokenUrl;
          })
        );
    } else {
      return of(null);
    }
  }

  canDisplayPolicyAccidentByMMobile(policy: PolicyAccident) {
    if (!this.isPolicyFreeTime(policy)) {
      return this.filterAccidentPolicyByMMobile(policy);
    }
    return (this.mmobileHelper.getCustomConfigData('tarjetas', 'tarjetaAccidentes', 'canDisplayPolicyFreeTime') as boolean)
      || false;
  }

  filterCarPolicyByMMobile(policy) {
    const companyId = padZeros(policy.companyId, 3);
    const carCard =
      this.mmobileHelper.getCustomConfigData('tarjetas', 'tarjetaAutos') || {};
    return (carCard.CIAS || []).some(c => padZeros(c, 3) === companyId);
  }

  filterHealthPolicyByMMobile(policy) {
    const companyId = padZeros(policy.companyId, 5);
    const product = padZeros(policy.productCode, 3);
    const healthCard =
      this.mmobileHelper.getCustomConfigData('tarjetas', 'tarjetaSalud') || {};
    return (
      (healthCard.producto || []).some(p => padZeros(p, 3) === product) &&
      (healthCard.CIAS || []).some(c => padZeros(c, 5) === companyId)
    );
  }

  filterInvestmentPolicyByMMobile(policy) {
    const product = padZeros(policy.department, 5);
    const investmentCard =
      this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaCarterasInversion'
      ) || {};
    return (investmentCard.producto || []).some(
      p => padZeros(p, 5) === product
    );
  }

  filterPensionPolicyByMMobile(policy) {
    const product = padZeros(policy.productCode, 5);
    const pensionCard =
      this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaPlanesPensiones'
      ) || {};
    return (pensionCard.producto || []).some(p => padZeros(p, 5) === product);
  }

  filterHomePolicyByMMobile(policy: Policy) {
    const homeCard = this.mmobileHelper.getCustomConfigData('tarjetas', 'tarjetaHogar') || {};
    return this.filterByRemoteCompanyIdAndDeparment(policy as PolicyHome, homeCard);
  }

  filterAccidentPolicyByMMobile(policy: Policy) {
    const accidentCard = this.mmobileHelper.getCustomConfigData('tarjetas', 'tarjetaAccidentes') || {};
    return this.filterByRemoteCompanyIdAndDeparment(policy as PolicyAccident, accidentCard);
  }

  filterDeathPolicyByMMobile(policy: Policy) {
    const deathCard = this.mmobileHelper.getCustomConfigData('tarjetas', 'tarjetaDecesos') || {};
    return this.filterByRemoteCompanyIdAndDeparment(policy as PolicyDeath, deathCard);
  }

  filterOtherPolicyByMMobile(policy: Policy) {
    const otherCard = this.mmobileHelper.getCustomConfigData('tarjetas', 'tarjetaOtros') || {};
    return this.filterByRemoteCompanyIdAndDeparment(policy as PolicyOther, otherCard);
  }

  getLastReceipt(policy: Policy) {
    return this.getReceipts(policy).pipe(
      switchMap(list => from(list)),
      first(receipts => receipts.last)
    );
  }

  getReceipts(policy: Policy): Observable<PolicyReceipt[]>{
    const receiptsList$ = this.getReceiptsByType(policy);
    return receiptsList$.pipe(
      map(receipts =>
        receipts?.filter(receipt => +receipt.amount > 0)));
  }

  getReceiptsByType(policy: Policy): Observable<PolicyReceipt[]> {
    switch (policy.policyBaseType) {
      case 'car': {
        return this.carPoliciesService.getCarReceipts(policy);
      }
      case 'home': {
        return this.homePoliciesService.getHomeReceipts(policy);
      }
      case 'other': {
        return this.otherPoliciesService.getOtherReceipts(policy);
      }
      case 'life': {
        return this.lifePoliciesService.getLifeReceipts(policy);
      }
      case 'saving': {
        return this.savingPoliciesService.getSavingReceipts(policy);
      }
      case 'health': {
        return this.healthPoliciesService.getHealthReceipts(policy);
      }
      case 'accident': {
        return this.accidentPoliciesService.getAccidentReceipts(policy);
      }
      case 'death': {
        return this.deathPoliciesService.getDeathReceipts(policy);
      }
      default: {
        return of(null as PolicyReceipt[]);
      }
    }
  }

  getReceipt(policy: Policy, receipt: PolicyReceipt): Observable<any> {
    switch (policy.policyBaseType) {
      case 'car': {
        return this.carPoliciesService.getCarReceipt(policy, receipt.number);
      }
      case 'home': {
        return this.homePoliciesService.getHomeReceipt(policy, receipt.number);
      }
      case 'other': {
        return this.otherPoliciesService.getOtherReceipt(
          policy,
          receipt.number
        );
      }
      case 'life': {
        return this.lifePoliciesService.getLifeReceipt(policy, receipt.number);
      }
      case 'saving': {
        return this.savingPoliciesService.getSavingReceipt(
          policy,
          receipt.number
        );
      }
      case 'health': {
        return this.healthPoliciesService.getHealthReceipt(
          policy,
          receipt.number
        );
      }
      case 'accident': {
        return this.accidentPoliciesService.getAccidentReceipt(
          policy,
          receipt.number
        );
      }
      case 'death': {
        return this.deathPoliciesService.getDeathReceipt(
          policy,
          receipt.number
        );
      }
      default: {
        return of(null);
      }
    }
  }

  getPendingReceipts(policy: Policy, roParams: any): Observable<any> {
    switch (policy.policyBaseType) {
      case 'car': {
        return this.carPoliciesService.getCarPendingReceipts(
          policy.policyNumber,
          policy.companyId,
          roParams
        );
      }
      case 'home': {
        return this.homePoliciesService.getHomePendingReceipts(
          policy.policyNumber,
          policy.companyId,
          roParams
        );
      }
      case 'other': {
        return this.otherPoliciesService.getOtherPendingReceipts(
          policy.policyNumber,
          policy.companyId,
          roParams
        );
      }
      case 'health': {
        return this.healthPoliciesService.getHealthPendingReceipts(
          policy.policyNumber,
          policy.companyId,
          roParams
        );
      }
      case 'life': {
        return this.lifePoliciesService.getLifePendingReceipts(
          policy.policyNumber,
          roParams
        );
      }
      case 'saving': {
        return this.savingPoliciesService.getSavingPendingReceipts(
          policy.policyNumber,
          roParams
        );
      }
      case 'accident': {
        return this.accidentPoliciesService.getAccidentPendingReceipts(
          policy.policyNumber,
          policy.companyId,
          roParams
        );
      }
      case 'death': {
        return this.deathPoliciesService.getDeathPendingReceipts(
          policy.policyNumber,
          policy.companyId,
          roParams
        );
      }
      default: {
        return of(null);
      }
    }
  }

  getSinisters(policy: Policy, clearCache?: boolean): Observable<any> {
    switch (policy.policyBaseType) {
      case 'car': {
        return this.carPoliciesService.getCarSinisters(policy, clearCache);
      }
      case 'home': {
        return this.homePoliciesService.getHomeSinisters(policy);
      }
      case 'other': {
        return this.otherPoliciesService.getOtherSinisters(policy);
      }
      case 'accident': {
        return this.accidentPoliciesService.getAccidentSinisters(policy);
      }
      case 'death': {
        return this.deathPoliciesService.getDeathSinisters(policy);
      }
      default: {
        return of(null);
      }
    }
  }

  getSinister(policy: Policy, sinister: PolicySinister): Observable<any> {
    if (policy.policyBaseType === 'car') {
      return this.carPoliciesService.getCarSinister(
        policy,
        sinister.code,
        sinister.hasServiceCenter
      );
    } else {
      return of(null);
    }
  }

  getExpedients(policy: Policy, sinister: PolicySinister): Observable<PolicyExpedient[]> {
    switch (policy.policyBaseType) {
      case 'home': {
        return this.homePoliciesService.getHomeExpedients(
          policy,
          sinister.code
        );
      }
      case 'other': {
        return this.otherPoliciesService.getOtherExpedients(
          policy,
          sinister.code
        );
      }
      case 'accident': {
        return this.accidentPoliciesService.getAccidentExpedients(
          policy,
          sinister.code
        );
      }
      case 'death': {
        return this.deathPoliciesService.getDeathExpedients(
          policy,
          sinister.code
        );
      }
      default: {
        return of(null);
      }
    }
  }

  getReceiptDocument(
    policy: Policy,
    { number: receiptNumber }: PolicyReceipt
  ): Observable<Document> {
    const params = this.or.calculateDocumentROParams(policy, {
      type: DocumentTypes.RECEIPT,
      payload: { receiptNumber }
    });
    switch (policy.policyBaseType) {
      case 'car': {
        return this.carPoliciesService.getCarReceiptDocument(
          policy.policyNumber,
          policy.companyId,
          receiptNumber,
          params
        );
      }
      case 'home': {
        return this.homePoliciesService.getHomeReceiptDocument(
          policy.policyNumber,
          policy.companyId,
          receiptNumber,
          params
        );
      }
      case 'other': {
        return this.otherPoliciesService.getOtherReceiptDocument(
          policy.policyNumber,
          policy.companyId,
          receiptNumber,
          params
        );
      }
      case 'accident': {
        return this.accidentPoliciesService.getAccidentReceiptDocument(
          policy.policyNumber,
          policy.companyId,
          receiptNumber,
          params
        );
      }
      case 'death': {
        return this.deathPoliciesService.getDeathReceiptDocument(
          policy.policyNumber,
          policy.companyId,
          receiptNumber,
          params
        );
      }
      case 'health': {
        return this.healthPoliciesService.getHealthReceiptDocument(
          policy.policyNumber,
          policy.companyId,
          receiptNumber,
          params
        );
      }
      default: {
        return of(null);
      }
    }
  }

  getParticularConditions(policy: Policy): Observable<Document> {
    const params = this.or.calculateDocumentROParams(policy);
    switch (policy.policyBaseType) {
      case 'car': {
        return this.carPoliciesService.getCarParticularConditions(
          policy.policyNumber,
          params
        );
      }
      case 'home': {
        return this.homePoliciesService.getHomeParticularConditions(
          policy.policyNumber,
          params
        );
      }
      case 'other': {
        return this.otherPoliciesService.getOtherParticularConditions(
          policy.policyNumber,
          params
        );
      }
      case 'health': {
        return this.healthPoliciesService.getHealthParticularConditions(
          policy.policyNumber,
          policy.productCode,
          params
        );
      }
      case 'accident': {
        return this.accidentPoliciesService.getAccidentParticularConditions(
          policy.policyNumber,
          params
        );
      }
      case 'death': {
        return this.deathPoliciesService.getDeathParticularConditions(
          policy.policyNumber,
          params
        );
      }
      default: {
        return of(null);
      }
    }
  }

  getGeneralConditions(policy: Policy): Observable<Document> {
    let params = this.or.calculateDocumentROParams(policy);
    switch (policy.policyBaseType) {
      case 'car': {
        return this.carPoliciesService.getCarGeneralConditions(
          policy.policyNumber,
          policy.generalConditionsCode,
          params
        );
      }
      case 'home':
      case 'death':
      case 'accident': {
        params = params.append(
          'generalConditionsCode',
          policy.generalConditionsCode
        );
        return this.homePoliciesService.getHomeGeneralConditions(
          policy.policyNumber,
          params
        );
      }
      case 'other': {
        return this.otherPoliciesService.getOtherGeneralConditions(
          policy.policyNumber,
          params,
          policy.generalConditionsCode
        );
      }
      case 'health': {
        return this.healthPoliciesService.getHealthGeneralConditions(
          policy.policyNumber,
          policy.productCode,
          policy.generalConditionsCode,
          params
        );
      }
      case 'life': {
        return this.lifePoliciesService.getLifeGeneralConditions(
          policy.policyNumber,
          params,
          policy.modalityId
        );
      }
      case 'saving': {
        return this.savingPoliciesService.getSavingGeneralConditions(
          policy.policyNumber,
          params,
          policy.modalityId
        );
      }
      default: {
        return of(null);
      }
    }
  }

  getRenewalNotice(policy: Policy): Observable<Document> {
    switch (policy.policyBaseType) {
      case 'home': {
        return this.homePoliciesService.getHomeRenewalNotice(
          policy
        );
      }
      case 'car': {
        return this.carPoliciesService.getCarRenewalNotice(policy);
      }
      case 'life': {
        return this.lifePoliciesService.getLifeRenewalNotice(
          policy
        );
      }
      case 'saving': {
        return this.savingPoliciesService.getSavingRenewalNotice(
          policy
        );
      }
      case 'health': {
        return this.healthPoliciesService.getHealthRenewalNotice(
          policy
        );
      }
      case 'other': {
        return this.otherPoliciesService.getOtherRenewalNotice(
          policy
        );
      }
      case 'accident': {
        return this.accidentPoliciesService.getAccidentRenewalNotice(
          policy
        );
      }
      case 'death': {
        return this.deathPoliciesService.getDeathRenewalNotice(
          policy
        );
      }
      default: {
        return of(null);
      }
    }
  }

  duplicateHealthCard(
    insured: PolicyHealthInsured,
    cause: string,
    address: Address | string
  ): Observable<any> {
    const parsedAddress =
      typeof address === 'string'
        ? address
        : this.addressHelper.getAddressResume(address);
    return this.healthPoliciesService.duplicateHealthCard(
      insured,
      cause,
      parsedAddress
    );
  }

  getPolicyReportDocument(
    policy: Policy,
    policyReportDocumentType: PolicyReportDocumentType,
    exercise?: string,
    model?: string
  ) {
    if (policy.policyBaseType === 'life') {
      if (policyReportDocumentType === 'CER') {
        const params = this.or.calculateDocumentROParams(policy, {
          type: DocumentTypes.DUPLICATE_FISCAL_CERTIFICATE,
          payload: { model, exercise }
        });
        if (model) {
          return this.lifePoliciesService.getLifePolicyFiscalCertificate(
            policy.policyNumber,
            model,
            exercise,
            params
          );
        } else {
          return this.lifePoliciesService.getLifePolicyFiscalCertificates(
            policy.policyNumber,
            exercise,
            params
          );
        }
      } else {
        return of(null);
      }
    }
    if (policy.policyBaseType === 'saving') {
      const params = this.or.calculateDocumentROParams(policy);
      switch (policyReportDocumentType) {
        case 'CER': {
          if (model) {
            return this.lifePoliciesService.getLifePolicyFiscalCertificate(
              policy.policyNumber,
              model,
              exercise,
              params
            );
          } else {
            return this.lifePoliciesService.getLifePolicyFiscalCertificates(
              policy.policyNumber,
              exercise,
              params
            );
          }
        }
        case 'DUP': {
          return this.savingPoliciesService.getSavingPolicyBondDocument(
            policy,
            params
          );
        }
        case 'BROCHURE': {
          return this.savingPoliciesService.getSavingBrochurePdf(policy);
        }
        default: {
          return of(null);
        }
      }
    }
    if (policy.policyBaseType === 'collective-life') {
      return this.collectiveLifePoliciesService.getCollectivePolicyBondDocument(policy);
    }
  }

  getCivilLiabilityCertificate(policy: Policy) {
    return this.homePoliciesService.getCivilLiabilityCertificate(policy);
  }

  getAccidentCertificate(policy: Policy) {
    return this.homePoliciesService.getAccidentCertificate(policy);
  }

  formatPolicyNumber(value: string) {
    while (value.length < 15) {
      value = '0' + value;
    }
    return value;
  }

  formatDocumentNumber(documentNumber) {
    if (!documentNumber) {
      return '';
    }
    while (documentNumber.length < 15) {
      documentNumber = '0' + documentNumber;
    }
    return documentNumber;
  }

  checkHealthInsuredIsValid(
    healthPolicy: PolicyHealth,
    insured: PolicyHealthInsured
  ) {
    if (!healthPolicy || !insured) return false;

    const currentInsuredIsHolder = healthPolicy.figure === 'TITULAR';
    const isCurrentInsured = this.isCurrentInsured(insured);
    const insuredAge = getAge(insured.birthdate);
    const insuredHasPermit = insured.cessionOfPermits;

    if (currentInsuredIsHolder) {
      return isCurrentInsured || insuredAge < 16 || insuredHasPermit;
    } else {
      return isCurrentInsured && insuredAge >= 16;
    }
  }

  isCurrentInsured(insured: PolicyHealthInsured) {
    const { user } = this.authMirror.authService.getState();
    if (!user) return false;

    const { documentNumber } = user;
    const insuredDocument = insured && insured.documentNumber;
    return (
      this.formatDocumentNumber(insuredDocument) ===
      this.formatDocumentNumber(documentNumber)
    );
  }

  getCurrentInsured(insureds: PolicyHealthInsured[]) {
    return (insureds || []).find(insured => this.isCurrentInsured(insured));
  }

  checkHealthProductCodeValidMMobile(productCode) {
    if (!productCode) {
      return false;
    }
    const validProductCodeListMMobile = this.mmobileHelper.getCustomConfigData(
      'authorizedHealthCodesForAuthorizations'
    );
    // MMobile valid productCode list
    if (validProductCodeListMMobile && validProductCodeListMMobile.length) {
      // check if productCode IS in list
      return validProductCodeListMMobile.indexOf(parseInt(productCode, 0)) > -1;
    } else {
      return false;
    }
  }

  checkHealthProductCodeValid(productCode) {
    if (!productCode) {
      return false;
    }
    const formattedProductCode = this.formatProductCode(productCode);
    const invalidProductCodeList = this.mmobileHelper.getCustomConfigData(
      'invalidProductCodeList'
    );
    // check if productCode IS NOT in list
    return invalidProductCodeList.indexOf(formattedProductCode) === -1;
  }

  checkHealthRenewalNoticeAvailable(policy: PolicyHealth) {
    return policy.comment === 'POLIZA' && policy.figure === 'TOMADOR';
  }

  checkYcarEcoActivityIsEnabled() {
    return this.mmobileHelper.checkIsFeatureEnabled(
      AllFeatureFlags.YCAR_ECO_ACTIVITY
    );
  }

  checkPolicyIsYcarEco(policy: PolicyCar) {
    return policy?.policyType === PolicyCarType.YCAR || policy?.policyType === PolicyCarType.ECO;
  }

  getYcarEcoActivitySSOUrl(policy: PolicyCar) {
    const roParams = this.or.setAutoParams(policy);
    return this.carPoliciesService.getYcarEcoActivitySSOUrl(policy?.policyNumber, roParams);
  }

  private aciCompaniesEnabledDepartmentsFilter(availablePolicy: PolicyOther, aciCompaniesEnabledDepartments: string[]) {
    return (availablePolicy.aciCompany &&
      aciCompaniesEnabledDepartments &&
      aciCompaniesEnabledDepartments.find(
        (element: string) =>
          element === availablePolicy.department
      ));
  }

  private formatProductCode(productCode: string) {
    let code = '';
    if (productCode) {
      let foundfirstDigit = false;
      let i = 0;
      while (!foundfirstDigit && i < productCode.length) {
        if (productCode.charAt(i) !== '0') {
          foundfirstDigit = true;
          code = productCode.slice(i);
        }
        i++;
      }
    }
    return code;
  }

  exchangeCarPolicySuperbonus(
    policy: PolicyCar,
    paymentMethod: SuperbonusOfferType,
    assistancePhoneNumber: string,
    headerParams?: any
  ) {
    return this.carPoliciesService.exchangeSuperbonus(
      policy.policyNumber,
      paymentMethod,
      assistancePhoneNumber,
      headerParams
    );
  }

  checkPolicyHasSuperbonus(policy: PolicyCar) {
    if (
      !policy ||
      !policy.superbonusData ||
      !policy.superbonusData.statusCode
    ) {
      return false;
    }
    const statusCode = policy.superbonusData.statusCode;
    return (
      statusCode === 'CG' ||
      statusCode === 'LM' ||
      statusCode === 'TM' ||
      (statusCode === 'LQ' &&
        this.checkCarPolicySuperbonusLQStatusIsAvailable(
          policy.superbonusData.statusDate
        ))
    );
  }

  isPolicyAccident = (p: PolicyBase): p is PolicyAccident => p.policyBaseType === 'accident';

  isPolicyCar = (p: PolicyBase): p is PolicyCar => p.policyBaseType === 'car';

  isPolicyDeath = (p: PolicyBase): p is PolicyDeath => p.policyBaseType === 'death';

  isPolicyHealth = (p: PolicyBase): p is PolicyHealth => p.policyBaseType === 'health';

  isPolicyHealthCollective = (p: PolicyHealth) => p.policyBaseType === 'health' && p.comment === 'GRUPO';

  isPolicyHome = (p: PolicyBase): p is PolicyHome => p.policyBaseType === 'home';

  isPolicyInvestmentNova = (p: PolicyBase): p is PolicyInvestmentNova => p.policyBaseType === 'investment-nova';

  isPolicyInvestment = (p: PolicyBase): p is PolicyInvestmentNova => p.policyBaseType === 'investment';

  isPolicyLife = (p: PolicyBase): p is PolicyLife => p.policyBaseType === 'life';

  isPolicyOther = (p: PolicyBase): p is PolicyOther => p.policyBaseType === 'other';

  isPolicyPension = (p: PolicyBase): p is PolicyPension => p.policyBaseType === 'pension';

  isPolicySaving = (p: PolicyBase): p is PolicySaving => p.policyBaseType === 'saving';

  isPolicyFreeTime(p: PolicyAccident) {
    const department = padZeros(p.department, 3);
    const productDescription = p.product;
    return department === '052' && productDescription === 'Accidentes Free Time';
  }

  private checkCarPolicySuperbonusLQStatusIsAvailable(statusDate: string) {
    if (!statusDate) {
      return false;
    }
    const currentDate = moment();
    const statusDateMoment = moment(statusDate, 'YYYY-MM-DD');
    return (
      currentDate.diff(statusDateMoment, 'months', true) <=
      SUPERBONUS_LQ_STATUS_AVAILABILITY_TIME_IN_MONTHS
    );
  }

  policyHasAnyDownloadableResumeDocument(policy: Policy) {
    if (!policy?.configuration?.operatives?.documents) return false;
    return this.isThereAnyDownloadableResumeDocument(
      policy?.configuration?.operatives?.documents
    );
  }

  policyHasAnyDownloadableDetailDocument(policy: Policy) {
    if (!policy?.configuration?.operatives?.documents) return false;
    return this.isThereAnyDownloadableDetailDocument(
      policy?.configuration?.operatives?.documents
    );
  }

  isThereAnyDownloadableResumeDocument(policyOperativeDocumentsConfig: PolicyOperativeDocumentsConfig) {
    return policyOperativeDocumentsConfig && Object.values(policyOperativeDocumentsConfig).some(document => document?.resume);
  }

  isThereAnyDownloadableDetailDocument(policyOperativeDocumentsConfig: PolicyOperativeDocumentsConfig) {
    return policyOperativeDocumentsConfig && Object.values(policyOperativeDocumentsConfig).some(document => document?.detail);
  }


  islifeCycleFutureProgramPolicy(policy: PolicyPension) {
    return policy?.belongsToLifeCycle && policy?.lifeCycleCode === LifeCyclePolicyType.FUTURE_PROGRAM;
  }

  isLifeCycleEPSVPolicies(policy: PolicyPension) {
    return policy?.belongsToLifeCycle && policy?.lifeCycleCode === LifeCyclePolicyType.EPSV;
  }

  private filterByRemoteCompanyIdAndDeparment(
    policy: PolicyHome | PolicyAccident | PolicyDeath | PolicyOther,
    card: { CIAS: string[], ramo: string[] }
  ) {
    const companyId = padZeros(policy.companyId, 3);
    const department = padZeros(policy.department, 3);
    const remoteCompanyId = card.CIAS as string[] || [];
    const remoteDepartment = card.ramo as string[] || [];
    return (
      remoteDepartment.some(d => padZeros(d, 3) === department) &&
      remoteCompanyId.some(c => padZeros(c, 3) === companyId)
    );
  }

}
